import CustomInput from "@/components/inputs/custom-input";
import CustomTextArea from "@/components/inputs/custom-textarea";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useContactSupport } from "@/hooks/use-contact-support";
import { EMAIL_REGEX, PHONE_REGEX } from "@/lib/constants";
import type { ContactFormState, FormErrors, SupportInfo } from "@/lib/types";
import { Mail, Phone, UserRound } from "lucide-react";

const ContactForm = () => {
  const {
    form,
    errors,
    setErrors,
    updateField,
    contactSupportMutation: { mutate, isPending },
  } = useContactSupport();

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nextErrors: FormErrors = {};
    (Object.keys(form) as Array<keyof ContactFormState>).forEach((field) => {
      if (!form[field].trim()) {
        nextErrors[field] = "This field is required";
      } else if (field === "email" && !EMAIL_REGEX.test(form.email)) {
        nextErrors.email = "Please enter a valid email address";
      } else if (field === "phone" && !PHONE_REGEX.test(form.phone)) {
        nextErrors.phone = "Phone must start with + followed by digits";
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload: SupportInfo = {
      Email: "support@sakeena.io",
      Purpose: "web-support",
      Meta: {
        Name: form.name,
        Phone: form.phone,
        Message: form.message,
        Subject: `[sakeena-web] ${form.name}`,
        ReplyTo: form.email,
      },
    };

    mutate(payload);
  };

  return (
    <div className="card px-6 py-8 sm:px-10 sm:py-14">
      <form noValidate onSubmit={handleSubmit}>
        <fieldset className=" flex flex-col gap-5" disabled={isPending}>
          <div className=" grid grid-cols-1 gap-5 sm:grid-cols-2">
            <CustomInput
              label="What's your name?"
              labelClassName="text-sm"
              leftIcon={UserRound}
              id="name"
              name="name"
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              error={errors.name}
            />

            <CustomInput
              label="What's your email address?"
              labelClassName="text-sm"
              leftIcon={Mail}
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              error={errors.email}
            />
          </div>

          <CustomInput
            label="What's your phone number?"
            labelClassName="text-sm"
            leftIcon={Phone}
            id="phone"
            name="phone"
            type="tel"
            placeholder="+13653550000"
            value={form.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            error={errors.phone}
          />

          <CustomTextArea
            label="Message"
            labelClassName="text-sm"
            id="message"
            name="message"
            placeholder="How can we help?"
            rows={5}
            value={form.message}
            onChange={(e) => updateField("message", e.target.value)}
            error={errors.message}
          />

          <Button size="xl" type="submit" className="w-full">
            {isPending ? (
              <>
                <Spinner data-icon="inline-start" />
                Sending...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </fieldset>
      </form>
    </div>
  );
};

export default ContactForm;
